import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { UploadManagerService } from './upload-manager.service';
import { UploadItem, UploadStatus } from './models/upload-item.model';

@Component({
  selector: 'app-upload-manager',
  standalone: true,
  imports: [CommonModule, DragDropModule],
  templateUrl: './upload-manager.component.html',
  styleUrls: ['./upload-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadManagerComponent implements OnInit {
  private uploadManager = inject(UploadManagerService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  uploads: UploadItem[] = [];
  isCollapsed = false;
  isVisible = false;

  /** CDK drag initial position — widget starts at bottom-right via CSS,
   *  but we track the free-drag offset so it persists during the session. */
  dragPosition = { x: 0, y: 0 };

  // ─────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.uploadManager.uploads$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(uploads => {
        this.uploads = uploads;
        // Auto-show whenever a new upload is queued
        if (uploads.length > 0) {
          this.isVisible = true;
          this.isCollapsed = false;
        }
        this.cdr.markForCheck();
      });
  }

  // ─────────────────────────────────────────────────────────────
  // Computed
  // ─────────────────────────────────────────────────────────────

  /** Number of uploads that have reached a terminal state */
  get doneCount(): number {
    return this.uploads.filter(u => this.isTerminal(u.status)).length;
  }

  /** True when every upload in the queue has finished */
  get allDone(): boolean {
    return this.uploads.length > 0 && this.doneCount === this.uploads.length;
  }

  /** Summary used by the widget header — title + badge */
  get uploadSummary(): { title: string; badge: string } {
    const total     = this.uploads.length;
    const uploading = this.uploads.filter(u => this.isActive(u.status)).length;
    const failed    = this.uploads.filter(u => u.status === 'failed' || u.status === 'aborted').length;
    const success   = this.uploads.filter(u => u.status === 'completed').length;

    let title: string;
    if (uploading > 0) {
      title = 'Uploading files...';
    } else if (failed > 0) {
      title = 'Upload completed with errors';
    } else {
      title = 'All files uploaded';
    }

    const badge = `${success + failed}/${total}`;
    return { title, badge };
  }

  // ─────────────────────────────────────────────────────────────
  // Template helpers
  // ─────────────────────────────────────────────────────────────

  /**
   * Resolve the status label for an upload item.
   * Uses the caller-provided label if set, otherwise falls back to
   * a generic label derived from the status enum.
   */
  getStatusLabel(item: UploadItem): string {
    if (item.statusLabel) return item.statusLabel;
    switch (item.status) {
      case 'uploading':   return 'Uploading...';
      case 'processing':  return 'Processing...';
      case 'completed':   return 'Completed';
      case 'failed':      return 'Failed';
      case 'aborted':     return 'Aborted';
      default:            return '';
    }
  }

  /** True when the upload is still running (not yet in a terminal state) */
  isActive(status: UploadStatus): boolean {
    return status === 'uploading' || status === 'processing';
  }

  /** True when the upload has reached a final state (no further updates expected) */
  isTerminal(status: UploadStatus): boolean {
    return status === 'completed' || status === 'failed' || status === 'aborted';
  }

  // ─────────────────────────────────────────────────────────────
  // User actions
  // ─────────────────────────────────────────────────────────────

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  /**
   * Hide the widget.
   * Active uploads continue running in the background — they will
   * update the service state and the widget will reappear when
   * a new upload is queued.
   */
  closeWidget(): void {
    this.isVisible = false;
  }

  /**
   * Abort a CSV upload.
   * - Fires the AbortController signal (cancels in-flight HTTP request)
   * - Updates status to 'aborted' in the service
   *
   * Note: the caller module is responsible for calling the backend abort API.
   * Use UploadManagerService.getBackendId(id) to retrieve the backendUploadId.
   */
  abortUpload(uploadId: string): void {
    this.uploadManager.abortUpload(uploadId);
  }

  removeUpload(uploadId: string): void {
    this.uploadManager.removeUpload(uploadId);
  }
}
